import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { AbstractControl, NgForm } from "@angular/forms";

import { lastValueFrom, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import {
  PaginationOffsetLimit,
  UnreachableCaseError,
} from "../../../../harmony/core";

import { GetLandingPageSummariesInteractor } from "../../../features/landing-page/domain/interactors/get-landing-page-summaries.interactor";
import { LandingPageSummary } from "../../../features/landing-page/domain/models/landing-page.summary";

import { UrlPrefixes } from "../../../shared/models/appData";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { CampaignLandingPageLinkType } from "../../../shared/enums/campaign.enums";
import { BrandService } from "../../../shared/services/api/brand.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { CustomFormValidators } from "../../../shared/validators/custom-form-validators";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";

enum OptionsState {
  AllEnabled = "all-enabled",
  BrandProvidedDisabled = "brand-provided-disabled",
  NonBrandProvidedDisabled = "non-brand-provided-disabled",
}

@Component({
  selector: "app-brand-campaign-landing-page",
  templateUrl: "./brand-campaign-landing-page.component.html",
  styleUrl: "./brand-campaign-landing-page.component.scss",
})
export class BrandCampaignLandingPageComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild("landingPageForm") protected landingPageForm!: NgForm;
  @ViewChild("landingPageLink") protected landingPageLinkInput!: ElementRef;

  protected readonly LinkType = CampaignLandingPageLinkType;
  protected readonly UrlPrefixes = UrlPrefixes;
  protected readonly URL_VALIDATION_PATTERN =
    CustomFormValidators.URL_WITH_OBLIGATORY_NO_PROTOCOL_PATTERN;
  protected availableLandingPages?: PaginationOffsetLimit<LandingPageSummary>;
  protected campaign: BrandCampaign;
  protected hasBrandProvidedPartnerUrlFeatureEnabled: boolean;
  protected isLoading = true;
  protected saving = false;
  protected savingError = false;

  private readonly LIMIT_LANDING_PAGES = 100;
  private readonly subscription = new Subscription();
  private optionsState: OptionsState = OptionsState.AllEnabled;

  constructor(
    private readonly notificationService: NotificationService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly getLandingPages: GetLandingPageSummariesInteractor,
    private readonly brandService: BrandService,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
    this.hasBrandProvidedPartnerUrlFeatureEnabled =
      this.brandService.hasBrandProvidedPartnerUrlFeatureEnabled();
  }

  public async ngOnInit(): Promise<void> {
    const [campaign, availableLandingPages] = await Promise.all([
      // Get the campaign so we have the latest number of partners statistics
      lastValueFrom(
        this.brandCampaignService.getBrandCampaign(
          this.campaign.brand.id,
          this.campaign.id,
        ),
      ),
      this.getLandingPages.execute(0, this.LIMIT_LANDING_PAGES),
    ]);

    this.campaign = campaign;
    this.availableLandingPages = availableLandingPages;
    this.isLoading = false;
    this.optionsState =
      this.campaign.totalNumberOfPartners === 0
        ? OptionsState.AllEnabled
        : this.campaign.landingPageLinkType ===
            CampaignLandingPageLinkType.BrandProvidedPartnerUrl
          ? OptionsState.NonBrandProvidedDisabled
          : OptionsState.BrandProvidedDisabled;
  }

  public ngAfterViewInit(): void {
    this.listenFormChanges();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  protected get landingPageLinkControl(): AbstractControl | undefined {
    return this.landingPageForm
      ? this.landingPageForm.form.controls.landingPageLink
      : undefined;
  }

  protected isOptionDisabled(type: CampaignLandingPageLinkType): boolean {
    if (this.isLoading) {
      return true;
    }

    switch (this.optionsState) {
      case OptionsState.AllEnabled:
        return false;

      case OptionsState.BrandProvidedDisabled:
        return type === CampaignLandingPageLinkType.BrandProvidedPartnerUrl;

      case OptionsState.NonBrandProvidedDisabled:
        return type !== CampaignLandingPageLinkType.BrandProvidedPartnerUrl;

      default:
        throw new UnreachableCaseError(this.optionsState);
    }
  }

  private listenFormChanges(): void {
    const formSubscription = this.landingPageForm.form.valueChanges
      .pipe(debounceTime(400))
      .subscribe(() => {
        if (this.campaign.landingPageLinkType === this.LinkType.OneSingleLink) {
          this.landingPageLinkInput.nativeElement.focus();
          this.landingPageForm.form.controls.landingPageLink.markAsTouched();
        }

        if (
          this.landingPageForm.form.valid &&
          this.landingPageForm.form.dirty
        ) {
          this.updateCampaign();
        }
      });

    this.subscription.add(formSubscription);
  }

  private updateCampaign(): void {
    this.saving = true;
    this.savingError = false;

    if (
      this.campaign.landingPageLinkType !== this.LinkType.CreatedLandingPage
    ) {
      // TODO: Remove `as any`. Fix `landingPageId` type.
      this.campaign.landingPageId = null as any;
    }

    this.brandCampaignService.updateBrandCampaign(this.campaign).subscribe(
      () => {
        this.saving = false;
      },
      () => {
        this.notificationService.error("shared.changesCouldNotBeSaved");
        this.saving = false;
        this.savingError = true;
      },
    );
  }

  protected formatLandingPageLinkUrl(): void {
    if (this.campaign.landingPageLink) {
      this.campaign.landingPageLink = this.campaign.landingPageLink.replace(
        " ",
        "",
      );

      for (const prefix of UrlPrefixes) {
        this.campaign.landingPageLink = this.campaign.landingPageLink.replace(
          prefix,
          "",
        );
      }
    }
  }
}
